import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { messList, messAttendanceMonthlyCalendar } from "../../api/Mess";
import { printElement } from "../master/Print";
import MessLayout from "./MessLayout";
import usePrivateLink from "../config/usePrivateLink";

// Fixed display order; the grid only shows the meal rows actually present
// in the fetched data (so a mess that only marks "full_day" doesn't get
// three empty Breakfast/Lunch/Dinner rows for every person).
const MEAL_TYPE_ORDER = ["breakfast", "lunch", "dinner", "full_day"];
const MEAL_TYPE_LABELS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  full_day: "Full Day",
};

const fmtTime = (t) =>
  t
    ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

// Cell label + hover tooltip built from the { status, check_in_time,
// check_out_time } object the API now returns per day/meal.
const cellInfo = (entry) => {
  if (!entry?.status) return { mark: "", title: "" };
  if (entry.status === "absent") return { mark: "A", title: "Absent" };
  const inT = fmtTime(entry.check_in_time);
  const outT = fmtTime(entry.check_out_time);
  const title = outT
    ? `In: ${inT}  •  Out: ${outT}`
    : inT
      ? `In: ${inT}`
      : "Present";
  return { mark: "P", title };
};

export default function MessAttendanceList() {
    const hasAccess = usePrivateLink();
  
  const [loading, setLoading] = useState(false);
  const [messes, setMesses] = useState([]);
  const [data, setData] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState(0);
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7),
    type: "Student",
  });

  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Which meal types actually show up anywhere in this month's data.
  const mealTypesUsed = useMemo(() => {
    const found = new Set();
    data.forEach((item) => {
      Object.values(item?.days ?? {}).forEach((dayMeals) => {
        Object.keys(dayMeals ?? {}).forEach((meal) => found.add(meal));
      });
    });
    const ordered = MEAL_TYPE_ORDER.filter((m) => found.has(m));
    return ordered.length ? ordered : ["full_day"];
  }, [data]);

  const getMessList = async () => {
    setLoading(true);
    try {
      const response = await messList(`&limit=all`);
      if (response.data?.status) {
        setMesses(response?.data?.data);
        setFilters((prev) => ({
          ...prev,
          mess_id: response?.data?.data?.[0]?.id,
        }));
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getDataList = useCallback(async () => {
    if (!filters?.mess_id || !filters?.type || !filters?.month) return;
    setLoading(true);
    const [year, month] = filters.month.split("-").map(Number);
    try {
      const response = await messAttendanceMonthlyCalendar({
        mess_id: filters.mess_id,
        type: filters.type,
        month,
        year,
      });
      if (response.data?.status) {
        setData(response?.data?.data ?? []);
        setDaysInMonth(response?.data?.days_in_month ?? 0);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    getMessList();
  }, []);

  useEffect(() => {
    getDataList();
  }, [getDataList]);

  const selectedMess = messes?.find((m) => m.id == filters?.mess_id);
  const monthLabel = filters?.month
    ? new Date(`${filters.month}-01`).toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    })
    : "";

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row py-5">
                  <MessLayout  />
                  <div className="col-12">
                    <div className="card my-3">
                      <div className="card-body">
                        <h4 className="text-primary mb-0">
                          Monthly Attendance Report
                        </h4>
                        <hr />
                        <div className="row align-items-center">
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="mess_id"
                                id="mess_id"
                                className="form-select"
                                value={filters?.mess_id ?? ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    mess_id: e.target.value,
                                  })
                                }
                              >
                                {messes?.map((mess) => (
                                  <option key={mess.id} value={mess.id}>
                                    {mess.name}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor=""> Select Mess </label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="type"
                                id="type"
                                className="form-select"
                                value={filters?.type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    type: e.target.value,
                                  })
                                }
                              >
                                <option value="Student">Student</option>
                                <option value="Staff">Staff</option>
                                <option value="Guest">Guest</option>
                              </select>
                              <label htmlFor=""> Select Type </label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="month"
                                className="form-control"
                                name="month"
                                value={filters?.month}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    month: e.target.value,
                                  })
                                }
                              />
                              <label htmlFor="">Month </label>
                            </div>
                          </div>
                          <div className="col-md-3 ">
                            <button
                              type="button"
                              onClick={() => {
                                setFilters({
                                  month: new Date().toISOString().slice(0, 7),
                                  type: "Student",
                                  mess_id: messes?.[0]?.id,
                                });
                              }}
                              className="btn me-1 btn-outline-danger"
                            >
                              <i className="ri ri-restart-line"></i>
                            </button>

                            {hasAccess("/mess/print") && (
                              <button
                                className="btn btn-outline-info"
                                onClick={() =>
                                  printElement(
                                    "PrintDiv",
                                    `@page {
    size: A4 landscape;
  }`,
                                    0.7,
                                  )
                                }
                              >
                                Print
                              </button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-body">
                        <div className="table-responsive" id="PrintDiv">
                          {selectedMess && (
                            <div className="text-center mb-2">
                              <h4 className="text-primary mb-0">
                                {selectedMess.name}
                              </h4>
                              <div className="text-muted">
                                {monthLabel} &middot; {filters?.type}
                              </div>
                            </div>
                          )}
                          <div className="mb-2 small text-muted">
                            <span className="badge bg-success me-1">P</span>
                            Present (hover for check-in/out time)
                            <span className="badge bg-danger ms-3 me-1">A</span>
                            Absent
                          </div>
                          <table className="table table-bordered table-sm">
                            <thead>
                              <tr>
                                <th style={{ minWidth: 30 }}>#</th>
                                <th style={{ minWidth: 140 }}>Name</th>
                                <th style={{ minWidth: 90 }}>Meal</th>
                                {daysArray.map((day) => (
                                  <th key={day} className="text-center">
                                    {String(day).padStart(2, "0")}
                                  </th>
                                ))}
                                <th
                                  className="text-center"
                                  style={{ minWidth: 60 }}
                                >
                                  Present
                                </th>
                                <th
                                  className="text-center"
                                  style={{ minWidth: 60 }}
                                >
                                  Absent
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {data?.map((item, i) =>
                                mealTypesUsed.map((meal, mi) => (
                                  <tr
                                    key={`${item.enrollment_id}-${meal}`}
                                    style={
                                      mi === mealTypesUsed.length - 1
                                        ? {
                                          borderBottom: "1pt solid #0000007a",
                                        }
                                        : {}
                                    }
                                  >
                                    {mi === 0 && (
                                      <>
                                        <td
                                          rowSpan={mealTypesUsed.length}
                                          className="fw-bolder"
                                        >
                                          {i + 1}
                                        </td>
                                        <td
                                          rowSpan={mealTypesUsed.length}
                                          className="fw-bolder"
                                        >
                                          {item.name}
                                        </td>
                                      </>
                                    )}
                                    <td>{MEAL_TYPE_LABELS[meal]}</td>

                                    {daysArray.map((day) => {
                                      const { mark, title } = cellInfo(
                                        item?.days?.[day]?.[meal],
                                      );
                                      return (
                                        <td
                                          key={day}
                                          className={`text-center ${mark === "P"
                                              ? "text-success fw-bold"
                                              : mark === "A"
                                                ? "text-danger fw-bold"
                                                : ""
                                            }`}
                                          title={title}
                                        >
                                          {mark}
                                        </td>
                                      );
                                    })}

                                    {mi === 0 && (
                                      <>
                                        <td
                                          rowSpan={mealTypesUsed.length}
                                          className="text-center text-success fw-bolder"
                                        >
                                          {item.present_count}
                                        </td>
                                        <td
                                          rowSpan={mealTypesUsed.length}
                                          className="text-center text-danger fw-bolder"
                                        >
                                          {item.absent_count}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                )),
                              )}
                              {!data?.length && (
                                <tr>
                                  <td
                                    colSpan={5 + daysInMonth}
                                    className="text-center text-muted"
                                  >
                                    No active enrollments for this mess/type.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
